import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'

import { HOVER_ROW_OVERLAY_FILL } from 'features/charts-v2/chart-hover-constants'
import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import {
  barMinPointSize,
  getRatingBarColor,
  getSentimentBarColor,
  RATING_THRESHOLDS,
  RATING_ZONE_COLORS,
  SENTIMENT_THRESHOLDS,
  SENTIMENT_ZONE_COLORS,
  SentimentColorGroup,
} from 'features/charts-v2/constants'
import { useYAxisLabelStep } from 'features/charts-v2/hooks/use-y-axis-label-step'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
// bully ignore[shared-imports-features: rule's applies predicate is always_ts_file (tools/bully/policy/src/rules/rules/shared_imports_features/mod.rs:19), not scoped to configured shared_layer_paths — this file already imports several charts-v2 sibling modules on adjacent lines. follow-up: .agents/follow-ups/bully-typescript-false-positive-rules.md]
import { DEFAULT_SERIES_COLOR } from 'features/charts-v2/services/series-color-palette'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { RoundedHorizontalBar } from 'features/charts-v2/strategies/RoundedHorizontalBar'
import { type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import {
  computeSnappedBarDomain,
  generateScatterTicks,
  quantizeBarValuesToDisplayPrecision,
  resolveBarValueDomain,
} from 'features/charts-v2/utils/axis-calculations'
import {
  calculateHorizontalBarYAxisWidth,
  calculateMaxTextWidth,
  DEFAULT_X_AXIS_HEIGHT_PX,
  formatMetricAxisNumber,
  getMetricAxisDomain,
  HORIZONTAL_BAR_Y_AXIS_TRUNCATE_CHARS,
  MAX_Y_AXIS_LABELS,
} from 'features/charts-v2/utils/axis-tick-utils'
import { CustomYAxisTick } from 'features/charts-v2/utils/AxisTickUtils'
import {
  companionVolumeRow,
  countMetricRow,
  metricRow,
} from 'features/charts-v2/utils/build-hover-annotation'

/**
 * Format number for display in labels
 * - Sentiment/Rating: always 2 decimal places
 * - Whole numbers: no decimals with commas (e.g., 21,310)
 * - Decimals: max 2 decimal places with commas (e.g., 4.91, 3.5)
 */
const formatLabelValue = (value: number, metricType?: MetricType): string => {
  if (metricType === 'sentiment' || metricType === 'rating') {
    return value.toFixed(2)
  }
  if (Number.isInteger(value)) {
    return value.toLocaleString()
  }
  // Round to 2 decimal places and remove trailing zeros
  return parseFloat(value.toFixed(2)).toLocaleString()
}

/**
 * Custom label component for horizontal bar data points
 * Displays the value to the right of each bar, skipping based on `step` to match y-axis interval.
 */
const CustomHorizontalBarLabel = React.memo<{
  x?: number
  y?: number
  width?: number
  height?: number
  value?: number
  index?: number
  step?: number
  fontSizePx?: number
  metricType?: MetricType
}>(
  ({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    value,
    index = 0,
    step = 1,
    fontSizePx = 13,
    metricType,
  }) => {
    // Don't render label if value is null/undefined/0
    if (value == null || value === 0) return null
    // Skip labels that don't fall on the computed interval
    if (step > 1 && index % step !== 0) return null

    // For negative values, position label at the 0 origin (right side of bar)
    // For positive values, position label at the end of bar (right side)
    const isNegative = value < 0
    const labelX = isNegative ? x + 5 : x + width + 5

    return (
      <text
        x={labelX}
        y={y + height / 2}
        fill="#333"
        fontSize={fontSizePx}
        textAnchor="start"
        dominantBaseline="middle"
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {formatLabelValue(value, metricType)}
      </text>
    )
  },
)

CustomHorizontalBarLabel.displayName = 'CustomHorizontalBarLabel'

// Recharts horizontal bar chart event types
interface RechartsHorizontalBarMouseEvent {
  activePayload?: Array<{ payload: { name: string } }>
  activeLabel?: string
}

// Bar click data type
interface HorizontalBarClickData {
  payload?: Record<string, unknown>
}

/**
 * Styled container for the chart. Hover dimming is handled imperatively by
 * Container.tsx via data-row-name attributes — no CSS opacity rules here to
 * prevent double-application and opacity compounding.
 */
const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
`

interface HorizontalBarChartViewProps {
  transformedData: TransformedChartData
  onDataPointHover?: ChartProps['onDataPointHover']
  selectedMetrics: ChartProps['selectedMetrics']
  onSegmentClick?: ChartProps['onSegmentClick']
  fontSizePx: number
  showXAxisLabel: boolean
  showYAxisLabel: boolean
  showDatapointLabels: boolean
  yAxisOverflow: NonNullable<ChartProps['yAxisOverflow']>
  showSentimentZones: boolean
  showRatingZones: boolean
  xAxisDomain?: ChartProps['xAxisDomain']
  xAxisSynced?: boolean
  metricLabel: string
  commonMargin: ReturnType<BaseChartStrategy['getCommonMargin']>
  disableAnimation: boolean
}

// eslint-disable-next-line react-refresh/only-export-components -- internal helper component, not a public export
const HorizontalBarChartViewInner: React.FC<HorizontalBarChartViewProps> = ({
  transformedData,
  onDataPointHover,
  selectedMetrics,
  onSegmentClick,
  fontSizePx,
  showXAxisLabel,
  showYAxisLabel,
  showDatapointLabels,
  yAxisOverflow,
  showSentimentZones,
  showRatingZones,
  xAxisDomain,
  xAxisSynced,
  metricLabel,
  commonMargin,
  disableAnimation,
}) => {
  const chartData = transformedData.chartData!

  const { containerRef, step } = useYAxisLabelStep({
    itemCount: chartData.length,
    fontSizePx,
    maxLabels: MAX_Y_AXIS_LABELS,
    chromeHeightPx:
      commonMargin.top + commonMargin.bottom + DEFAULT_X_AXIS_HEIGHT_PX,
  })

  const isSentimentMetric = selectedMetrics[0] === 'sentiment'
  const isRatingMetric = selectedMetrics[0] === 'rating'
  const primaryMetricType = selectedMetrics[0]!

  // Compute a data-driven domain + ticks for sentiment/rating by snapping the
  // data range OUTWARD to the metric's natural steps (rating: whole units,
  // sentiment: quarter units) plus one step of headroom below the minimum,
  // clamped to the metric scale — e.g. rating values 4.58–4.61 → [3, 5].
  //
  // When "Sync X-axis values" is on, xAxisSynced is true and xAxisDomain carries
  // the cross-panel combined [min, max]. We feed that combined range into the same
  // snapping so both panels end up with the same domain that includes all bars
  // from every panel. When sync is off, we use this panel's own data range
  // (the existing per-panel behavior).
  let computedXDomain: [number, number] | undefined = undefined
  let computedXTicks: number[] | undefined = undefined
  if (isSentimentMetric || isRatingMetric) {
    const xValues = chartData
      .map((d) => d['count'] as number)
      .filter((v): v is number => typeof v === 'number')

    const baseRange: [number, number] | undefined =
      xAxisSynced && xAxisDomain
        ? xAxisDomain
        : xValues.length > 0
          ? [Math.min(...xValues), Math.max(...xValues)]
          : undefined

    if (baseRange) {
      const snappedDomain = computeSnappedBarDomain(
        baseRange,
        isSentimentMetric ? 'sentiment' : 'rating',
      )
      if (snappedDomain) {
        computedXDomain = snappedDomain
        // Filter ticks to the snapped domain so the axis stays within it
        computedXTicks = generateScatterTicks(
          snappedDomain[0],
          snappedDomain[1],
          primaryMetricType,
        ).filter((t) => t >= snappedDomain[0] && t <= snappedDomain[1])
      }
    }
  }

  // Prefer computed data-driven domain; fall back to synced prop then fixed
  // metric range. Whatever wins is guarded against a degenerate [v, v] domain
  // (all bars equal — e.g. the synced single-bar count chart) before Recharts
  // sees it: sentiment/rating arrive pre-guarded by computeSnappedBarDomain,
  // so the guard only changes the previously-broken equal-value count path.
  const effectiveXDomain = resolveBarValueDomain(
    computedXDomain ?? xAxisDomain ?? getMetricAxisDomain(selectedMetrics[0]!),
    primaryMetricType,
  )

  const isVolumeMetric = isSentimentMetric || isRatingMetric
  const isCountMetric =
    primaryMetricType === 'record_count' ||
    primaryMetricType === 'sentence_count'

  const tooltipTotal = React.useMemo(() => {
    if (isCountMetric) {
      return chartData.reduce(
        (sum, d) => sum + ((d['count'] as number) || 0),
        0,
      )
    }
    if (isVolumeMetric) {
      return chartData.reduce(
        (sum, d) => sum + ((d['volumeCount'] as number) || 0),
        0,
      )
    }
    return 0
  }, [chartData, isCountMetric, isVolumeMetric])

  const sentimentZones: Array<{ x1: number; x2: number; fill: string }> = []
  if (isSentimentMetric && showSentimentZones) {
    let domainMin: number
    let domainMax: number

    if (effectiveXDomain && effectiveXDomain.length === 2) {
      domainMin =
        typeof effectiveXDomain[0] === 'number' ? effectiveXDomain[0] : -1
      domainMax =
        typeof effectiveXDomain[1] === 'number' ? effectiveXDomain[1] : 1
    } else {
      const values = chartData.flatMap((d: Record<string, unknown>) => {
        const value = d['count'] ?? d['value']
        return typeof value === 'number' ? [value] : []
      })
      domainMin = values.length === 0 ? -1 : Math.min(...values)
      domainMax = values.length === 0 ? 1 : Math.max(...values)
    }

    if (domainMin < SENTIMENT_THRESHOLDS.NEGATIVE) {
      const x1 = Math.max(domainMin, -1)
      const x2 = Math.min(domainMax, SENTIMENT_THRESHOLDS.NEGATIVE)
      if (x2 > x1) {
        sentimentZones.push({
          x1,
          x2,
          fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.NEGATIVE],
        })
      }
    }

    if (
      domainMin < SENTIMENT_THRESHOLDS.POSITIVE &&
      domainMax > SENTIMENT_THRESHOLDS.NEGATIVE
    ) {
      const x1 = Math.max(domainMin, SENTIMENT_THRESHOLDS.NEGATIVE)
      const x2 = Math.min(domainMax, SENTIMENT_THRESHOLDS.POSITIVE)
      if (x2 > x1) {
        sentimentZones.push({
          x1,
          x2,
          fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.NEUTRAL],
        })
      }
    }

    if (domainMax > SENTIMENT_THRESHOLDS.POSITIVE) {
      const x1 = Math.max(domainMin, SENTIMENT_THRESHOLDS.POSITIVE)
      const x2 = Math.min(domainMax, 1)
      if (x2 > x1) {
        sentimentZones.push({
          x1,
          x2,
          fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.POSITIVE],
        })
      }
    }
  }

  if (isRatingMetric && showRatingZones) {
    let domainMin: number
    let domainMax: number

    if (effectiveXDomain && effectiveXDomain.length === 2) {
      domainMin =
        typeof effectiveXDomain[0] === 'number' ? effectiveXDomain[0] : 1
      domainMax =
        typeof effectiveXDomain[1] === 'number' ? effectiveXDomain[1] : 5
    } else {
      const values = chartData.flatMap((d: Record<string, unknown>) => {
        const value = d['count'] ?? d['value']
        return typeof value === 'number' ? [value] : []
      })
      domainMin = values.length === 0 ? 1 : Math.min(...values)
      domainMax = values.length === 0 ? 5 : Math.max(...values)
    }

    // Negative zone (below 3)
    if (domainMin < RATING_THRESHOLDS.NEGATIVE) {
      const x1 = Math.max(domainMin, 1)
      const x2 = Math.min(domainMax, RATING_THRESHOLDS.NEGATIVE)
      if (x2 > x1) {
        sentimentZones.push({ x1, x2, fill: RATING_ZONE_COLORS.negative })
      }
    }

    // Positive zone (above 4)
    if (domainMax > RATING_THRESHOLDS.POSITIVE) {
      const x1 = Math.max(domainMin, RATING_THRESHOLDS.POSITIVE)
      const x2 = Math.min(domainMax, 5)
      if (x2 > x1) {
        sentimentZones.push({ x1, x2, fill: RATING_ZONE_COLORS.positive })
      }
    }
  }

  const labels = chartData.map((d) => String(d['name'] ?? ''))
  const yAxisWidth = showYAxisLabel
    ? calculateHorizontalBarYAxisWidth(labels, yAxisOverflow, fontSizePx)
    : 10

  // Compute right margin wide enough to fully display the data-point label drawn
  // to the right of each bar (at x + width + 5). We measure the widest formatted
  // value using the same font the label renders at, then add the 5px gap plus an
  // 8px buffer. Math.max keeps us at or above the base 30px common margin.
  const labelFont = `${fontSizePx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
  const dataLabelStrings = showDatapointLabels
    ? chartData
        .map((d) => {
          const v = d['count'] as number | undefined
          return v != null && v !== 0
            ? formatLabelValue(v, primaryMetricType)
            : ''
        })
        .filter(Boolean)
    : []
  const maxDataLabelWidth =
    dataLabelStrings.length > 0
      ? calculateMaxTextWidth(dataLabelStrings, labelFont)
      : 0
  const LABEL_GAP = 5
  const LABEL_BUFFER = 8
  const rightMargin = showDatapointLabels
    ? Math.max(commonMargin.right, LABEL_GAP + maxDataLabelWidth + LABEL_BUFFER)
    : commonMargin.right

  // `step` is forwarded so thinned-out labels fade (interval={0} below keeps
  // every tick mounted) instead of Recharts mounting/unmounting them.
  const tickRenderer = (tickProps: {
    x: number
    y: number
    payload: { value: string }
    [k: string]: unknown
  }): JSX.Element => (
    <CustomYAxisTick
      {...tickProps}
      fontSizePx={fontSizePx}
      overflowMode={yAxisOverflow}
      truncateAtChars={HORIZONTAL_BAR_Y_AXIS_TRUNCATE_CHARS}
      wrapAtPx={yAxisWidth - 16}
      step={step}
    />
  )

  return (
    <ChartContainer ref={containerRef}>
      <ResponsiveContainer width="100%" height="100%" debounce={100}>
        <BarChart
          key={`chart-${effectiveXDomain?.[1] ?? 'auto'}`}
          data={chartData}
          layout="vertical"
          margin={{ ...commonMargin, right: rightMargin }}
          onMouseMove={
            ((data: RechartsHorizontalBarMouseEvent) => {
              if (data && data.activeLabel) {
                onDataPointHover?.(data.activeLabel)
              }
            }) as never
          }
          onMouseLeave={() => onDataPointHover?.(null)}
        >
          <CartesianGrid strokeDasharray="3 3" />

          {/* Sentiment background zones */}
          {sentimentZones.map((zone, index) => (
            <ReferenceArea
              key={`sentiment-zone-${index}`}
              x1={zone.x1}
              x2={zone.x2}
              fill={zone.fill}
              fillOpacity={1}
            />
          ))}

          {/* Y-axis is categorical (categories) - horizontal bars, custom tick prevents wrapping */}
          <YAxis
            type="category"
            dataKey="name"
            width={yAxisWidth}
            tick={showYAxisLabel ? tickRenderer : false}
            interval={0}
          />

          {/* X-axis is numeric (values) - at top */}
          <XAxis
            type="number"
            orientation="top"
            allowDecimals={isSentimentMetric || isRatingMetric}
            ticks={computedXTicks}
            tick={{ fontSize: fontSizePx }}
            tickFormatter={(value) =>
              formatMetricAxisNumber(value, primaryMetricType)
            }
            domain={effectiveXDomain || undefined}
            label={
              showXAxisLabel
                ? {
                    value: metricLabel,
                    position: 'insideTop',
                    offset: -5,
                    style: {
                      textAnchor: 'middle',
                      fill: '#666',
                      fontSize: fontSizePx,
                    },
                  }
                : undefined
            }
          />

          <Tooltip
            cursor={{ fill: HOVER_ROW_OVERLAY_FILL }}
            content={({
              active,
              payload: tooltipPayload,
              label: tooltipLabel,
            }) => {
              if (!active || !tooltipPayload?.length) return null
              const entry = tooltipPayload[0]
              if (!entry) return null
              const val = entry.value as number
              const dp = entry.payload as Record<string, unknown>

              const rows = []
              if (isVolumeMetric) {
                rows.push(metricRow(primaryMetricType, val))
                const volumeCount = dp['volumeCount'] as number | undefined
                const volRow = companionVolumeRow(
                  primaryMetricType,
                  volumeCount,
                  tooltipTotal,
                )
                if (volRow) rows.push(volRow)
              } else if (isCountMetric) {
                rows.push(countMetricRow(primaryMetricType, val, tooltipTotal))
              } else {
                rows.push(metricRow(primaryMetricType, val))
              }

              return (
                <ChartDatapointHoverAnnotation
                  title={String(tooltipLabel)}
                  rows={rows}
                  footer={null}
                  focusedField={null}
                />
              )
            }}
          />

          <Bar
            dataKey="count"
            name={metricLabel}
            fill={DEFAULT_SERIES_COLOR}
            shape={<RoundedHorizontalBar metricType={primaryMetricType} />}
            minPointSize={barMinPointSize}
            isAnimationActive={!showDatapointLabels && !disableAnimation}
            label={
              showDatapointLabels
                ? (labelProps: Record<string, unknown>) => (
                    <CustomHorizontalBarLabel
                      {...labelProps}
                      step={step}
                      fontSizePx={fontSizePx}
                      metricType={primaryMetricType}
                    />
                  )
                : false
            }
            onClick={(
              data: HorizontalBarClickData,
              _index: number,
              event: React.MouseEvent,
            ) => {
              if (onSegmentClick && data && data.payload) {
                onSegmentClick(
                  {
                    primaryBreakdown: data.payload['name'] as string,
                    value: data.payload['count'] as number,
                    payload: data.payload as Record<
                      string,
                      string | number | boolean | null
                    >,
                  },
                  event,
                )
              }
            }}
          >
            {(isSentimentMetric || isRatingMetric) &&
              chartData.map((entry, index) => {
                const value = (entry['count'] as number) || 0
                const fill = isSentimentMetric
                  ? getSentimentBarColor(value)
                  : getRatingBarColor(value)
                return <Cell key={`cell-${index}`} fill={fill} />
              })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

const HorizontalBarChartView = React.memo(HorizontalBarChartViewInner)

/**
 * Horizontal Bar Chart Strategy
 *
 * A horizontal bar chart where:
 * - X-axis is numeric (the value/metric)
 * - Y-axis is categorical (the category labels)
 *
 * This is the inverse of the regular BAR chart where X is categorical and Y is numeric.
 */
export class HorizontalBarChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.BAR_HORIZONTAL
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = []

  public transform(props: ChartProps): TransformedChartData {
    const breakdowns = props.selectedBreakdowns || []

    // Handle 0 breakdowns: show single bar with total
    if (breakdowns.length === 0) {
      return quantizeBarValuesToDisplayPrecision(
        dataTransformationService.transformForTotalOnly(
          props.data!,
          props.selectedMetrics,
        ),
        props.selectedMetrics,
      )
    }

    return quantizeBarValuesToDisplayPrecision(
      dataTransformationService.transformForBasicChart(
        props.data!,
        props.selectedMetrics,
        props.timeBucket,
        props.sortDirection,
      ),
      props.selectedMetrics,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      onDataPointHover,
      selectedMetrics,
      onSegmentClick,
      fontSizePx = 13,
      showXAxisLabel = true,
      showYAxisLabel = true,
      showDatapointLabels = true,
      yAxisOverflow = 'expand',
      showSentimentZones = true,
      showRatingZones = true,
      xAxisDomain,
      xAxisSynced,
      disableAnimation = false,
    } = props

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    return (
      <HorizontalBarChartView
        transformedData={transformedData}
        onDataPointHover={onDataPointHover}
        selectedMetrics={selectedMetrics}
        onSegmentClick={onSegmentClick}
        fontSizePx={fontSizePx}
        showXAxisLabel={showXAxisLabel}
        showYAxisLabel={showYAxisLabel}
        showDatapointLabels={showDatapointLabels}
        yAxisOverflow={yAxisOverflow}
        showSentimentZones={showSentimentZones}
        showRatingZones={showRatingZones}
        xAxisDomain={xAxisDomain}
        xAxisSynced={xAxisSynced}
        disableAnimation={disableAnimation}
        metricLabel={this.getMetricLabel([selectedMetrics[0]!])}
        commonMargin={this.getCommonMargin()}
      />
    )
  }

  public getTitle(): string {
    return 'Posts by Category'
  }
}
